import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { NavBar } from 'components/NavBar/NavBar'
import { RootState } from 'store'
import {
  Flex,
  H1WithBack,
  PageFooter,
  PageWrapper,
} from 'styling/GlobalStyles'
import { OtherText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { Pagination } from 'components/Pagination/Pagination'
import { formatNumbersForDisplay, getParams, handlePagination } from 'utils/helpers'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import useQueryPagination from 'hooks/useQueryPagination'
import { clearFuelLosses, getFuelLosses } from 'features/fuelManagement/fuelManagementSlice'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { AlarmBanner, AlarmContent, AlarmDot, AlarmText } from 'pages/Operations/Safety/Safety.styled'
import { FilterEnum, IFilter, LinkEnum } from 'components/Filter/SingleFilter'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { IFuelOpsLossRow } from 'types/fuelOps.interface'
import { mapFuelLoss } from 'pages/FuelManagement/Shared/fuelApiMappers'
import {
  criticalLossBannerText,
  findOpenCriticalLoss,
  formatGhs,
  fuelLossCostGhs,
  FUEL_LOSS_CLOSURE_CAPTION,
  FUEL_RESIDUAL_EXPLAINER,
} from '../Shared/fuelFormatters'
import { fuelLossCaseStatuses, fuelLossRootCauses, fuelLossSeverities } from '../fuelData'
import { LossCellEm, LossExplainerWrap, LossInvestigationCell } from './FuelLosses.styled'
import { CASE_STATUS_META, LossCaseModal, OUTCOME_META } from './LossCaseModal'

const STATUS_TONE: { [k in IFuelOpsLossRow['status']]: IStatusPill['status'] } = {
  'Under investigation': 'danger',
  Explained: 'success',
  'Written off — signed': 'neutral',
}

const columns: IHonestTableColumn<IFuelOpsLossRow>[] = [
  { id: 'date', header: 'DATE', cell: (item) => item.dateLabel },
  {
    id: 'severity',
    header: 'SEVERITY',
    cell: (item) => (
      <StatusPill status={item.severity === 'critical' ? 'danger' : 'warning'} dot={item.severity === 'critical'}>
        {item.severity === 'critical' ? 'Critical' : 'Warning'}
      </StatusPill>
    ),
  },
  {
    id: 'amount',
    header: 'AMOUNT',
    cell: (item) => <LossCellEm>{`${formatNumbersForDisplay(item.liters)} L`}</LossCellEm>,
  },
  {
    id: 'cost',
    header: 'COST',
    cell: (item) =>
      item.outcome === 'RECOVERED' ? (
        <StatusPill status='success'>Recovered</StatusPill>
      ) : (
        <StatusPill status={item.severity === 'critical' ? 'danger' : 'neutral'}>
          {formatGhs(item.costGhs ?? fuelLossCostGhs(item.liters))}
        </StatusPill>
      ),
  },
  { id: 'tank', header: 'TANK', cell: (item) => item.tank },
  { id: 'classification', header: 'CLASSIFICATION', cell: (item) => item.classification },
  { id: 'owner', header: 'OWNER', cell: (item) => item.owner },
  {
    id: 'status',
    header: 'STATUS',
    cell: (item) => {
      const meta = CASE_STATUS_META[item.caseStatus || 'OPEN']
      return (
        <StatusPill status={meta.tone} dot={item.caseStatus === 'PENDING_APPROVAL'}>
          {meta.label}
        </StatusPill>
      )
    },
  },
  {
    id: 'outcome',
    header: 'OUTCOME',
    cell: (item) =>
      item.outcome ? (
        <StatusPill status={OUTCOME_META[item.outcome].tone}>{OUTCOME_META[item.outcome].label}</StatusPill>
      ) : (
        <OtherText grey>—</OtherText>
      ),
  },
  {
    id: 'investigation',
    header: 'INVESTIGATION',
    minWidth: '14rem',
    cell: (item) => (
      <LossInvestigationCell>
        <StatusPill
          status={STATUS_TONE[item.status]}
          dot={item.status === 'Under investigation' && item.severity === 'critical'}
        >
          {item.status}
        </StatusPill>
        {item.note && <OtherText color={tokens.text.muted}>{item.note}</OtherText>}
      </LossInvestigationCell>
    ),
  },
]

export const FuelLosses = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [params, setParams] = useState<any>(getParams({ location }))
  const { fuelLosses } = useSelector((store: RootState) => store.fuelManagement)
  const [selectedLossId, setSelectedLossId] = useState<number | null>(null)

  useQueryPagination({ fetchFunctions: [getFuelLosses], cleanUpFunctions: [clearFuelLosses], params })

  const rows: IFuelOpsLossRow[] = useMemo(
    () => (fuelLosses?.data?.length ? fuelLosses.data.map(mapFuelLoss) : []),
    [fuelLosses],
  )

  const criticalLoss = findOpenCriticalLoss(rows)

  const filters: IFilter[] = [
    {
      type: FilterEnum.FIXED_LIST,
      name: 'Case status',
      propName: 'caseStatus',
      single: true,
      sd: true,
      filterOptions: fuelLossCaseStatuses.map((s) => ({ name: s.label, id: String(s.value) })),
    },
    {
      type: FilterEnum.FIXED_LIST,
      name: 'Severity',
      propName: 'severity',
      single: true,
      sd: true,
      filterOptions: fuelLossSeverities.map((s) => ({ name: s.label, id: String(s.value) })),
    },
    {
      type: FilterEnum.FIXED_LIST,
      name: 'Root cause',
      propName: 'rootCause',
      single: true,
      sd: true,
      filterOptions: fuelLossRootCauses.map((s) => ({ name: s.label, id: String(s.value) })),
    },
    {
      type: FilterEnum.ASYNC_SELECT,
      name: 'Owner',
      link: LinkEnum.USERS_BASIC,
      propName: 'ownerUserId',
      single: true,
    },
  ]

  return (
    <>
      <PageWrapper>
        <NavBar
          filterOptions={filters}
          enableTemporaryManualClosing
          setParams={setParams}
          params={params}
        />
        <Flex between mb='16'>
          <Flex column gap='0.25rem'>
            <H1WithBack title='Fuel Losses' arrow onClick={() => navigate('/fuel/dashboard')} />
            <OtherText grey>
              Residual variances — investigate, assign, and close with outcome + root cause
            </OtherText>
          </Flex>
        </Flex>
        {criticalLoss && (
          <AlarmBanner style={{ marginTop: 0, marginBottom: '1rem' }}>
            <AlarmContent>
              <AlarmDot />
              <AlarmText>{criticalLossBannerText(criticalLoss)}</AlarmText>
            </AlarmContent>
          </AlarmBanner>
        )}
        {!rows.length ? (
          <NoItems title='Fuel Losses' />
        ) : (
          <>
            <HonestTable
              columns={columns}
              data={rows}
              keyField='id'
              onClick={(item) => setSelectedLossId(item.id)}
            />
            <LossExplainerWrap>
              <OtherText color={tokens.text.muted}>{FUEL_LOSS_CLOSURE_CAPTION}</OtherText>
              <OtherText color={tokens.text.muted}>{FUEL_RESIDUAL_EXPLAINER}</OtherText>
            </LossExplainerWrap>
          </>
        )}
        {selectedLossId != null && (
          <LossCaseModal
            lossId={selectedLossId}
            closeModal={() => setSelectedLossId(null)}
            onChanged={() => dispatch(getFuelLosses(params) as any)}
          />
        )}
      </PageWrapper>
      <PageFooter>
        <Pagination
          setPage={(page) => handlePagination(page, setParams)}
          pageCount={Math.ceil(fuelLosses.totalPages)}
          forcePage={(params.page ? params.page : 1) - 1}
        />
      </PageFooter>
    </>
  )
}
